import { useState, useCallback, useEffect } from 'react';

export interface PlayerSelectionHook {
  selectedPlayerIds: Set<number>;
  
  // Selection handlers
  handleSelectPlayer: (playerId: number, checked: boolean) => void;
  handleSelectAll: (playerIds: number[], checked: boolean) => void;
  handleSelectAllFiltered: (filteredPlayerIds: number[]) => void;
  handleClearSelection: () => void;
  
  // Selection state helpers
  isPlayerSelected: (playerId: number) => boolean;
  areAllSelected: (playerIds: number[]) => boolean;
  areSomeSelected: (playerIds: number[]) => boolean;
  getSelectionCount: () => number;
  
  // Reset selection when dependencies change
  resetOnChange: () => void;
}

/**
 * Reusable hook for managing player selection state across different components
 * Centralizes selection logic to prevent code duplication and maintain consistency
 * 
 * @param options Configuration options for selection behavior
 * @returns Player selection state and handlers
 */
export function usePlayerSelection(options: {
  /** Clear selection when these dependencies change */
  clearOnChange?: any[];
  /** Maximum number of players that can be selected (0 = unlimited) */
  maxSelection?: number;
} = {}): PlayerSelectionHook {
  
  const { clearOnChange = [], maxSelection = 0 } = options;
  
  // Core selection state
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<Set<number>>(new Set());
  
  // Clear selection when dependencies change
  useEffect(() => {
    if (clearOnChange.length > 0) {
      setSelectedPlayerIds(new Set());
    }
  }, clearOnChange);
  
  // Individual player selection handler
  const handleSelectPlayer = useCallback((playerId: number, checked: boolean) => {
    setSelectedPlayerIds(prev => {
      const newSelected = new Set(prev);
      
      if (checked) {
        // Check max selection limit
        if (maxSelection > 0 && newSelected.size >= maxSelection) {
          return prev; // Don't add if at limit
        }
        newSelected.add(playerId);
      } else {
        newSelected.delete(playerId);
      }
      
      return newSelected;
    });
  }, [maxSelection]);
  
  // Select/deselect all from provided list
  const handleSelectAll = useCallback((playerIds: number[], checked: boolean) => {
    setSelectedPlayerIds(prev => {
      const newSelected = new Set(prev);
      
      if (checked) {
        // Add all players (respecting max limit if set)
        const currentSize = newSelected.size;
        const availableSlots = maxSelection > 0 ? maxSelection - currentSize : Infinity;
        
        playerIds.slice(0, availableSlots).forEach(id => newSelected.add(id));
      } else {
        // Remove all provided players
        playerIds.forEach(id => newSelected.delete(id));
      }
      
      return newSelected;
    });
  }, [maxSelection]);
  
  // Select all filtered players (adds to existing selection)
  const handleSelectAllFiltered = useCallback((filteredPlayerIds: number[]) => {
    setSelectedPlayerIds(prev => {
      const newSelected = new Set(prev);
      
      // Add all filtered players (respecting max limit if set)
      const currentSize = newSelected.size;
      const availableSlots = maxSelection > 0 ? maxSelection - currentSize : Infinity;
      
      filteredPlayerIds.slice(0, availableSlots).forEach(id => newSelected.add(id));
      
      return newSelected;
    });
  }, [maxSelection]);
  
  // Clear all selections
  const handleClearSelection = useCallback(() => {
    setSelectedPlayerIds(new Set());
  }, []);
  
  // Helper: Check if specific player is selected
  const isPlayerSelected = useCallback((playerId: number) => {
    return selectedPlayerIds.has(playerId);
  }, [selectedPlayerIds]);
  
  // Helper: Check if all players in list are selected
  const areAllSelected = useCallback((playerIds: number[]) => {
    return playerIds.length > 0 && playerIds.every(id => selectedPlayerIds.has(id));
  }, [selectedPlayerIds]);
  
  // Helper: Check if some (but not all) players in list are selected
  const areSomeSelected = useCallback((playerIds: number[]) => {
    return playerIds.some(id => selectedPlayerIds.has(id));
  }, [selectedPlayerIds]);
  
  // Helper: Get selection count
  const getSelectionCount = useCallback(() => {
    return selectedPlayerIds.size;
  }, [selectedPlayerIds]);
  
  // Helper: Reset selection when dependencies change (legacy compatibility)
  const resetOnChange = useCallback(() => {
    // This is handled by the useEffect above, but provided for explicit calls
    setSelectedPlayerIds(new Set());
  }, []);
  
  return {
    selectedPlayerIds,
    handleSelectPlayer,
    handleSelectAll,
    handleSelectAllFiltered,
    handleClearSelection,
    isPlayerSelected,
    areAllSelected,
    areSomeSelected,
    getSelectionCount,
    resetOnChange,
  };
} 